<template>
  <div class="mask" @click.self="onClickOutside">
    <div
      ref="dialogRef"
      class="dialog"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
      @keydown="onKeydown"
    >
      <div class="dialog__body">
        <p class="dialog__message">{{ messageText }}</p>
        <p v-if="phase === 'confirm'" class="dialog__note">
          {{ $t("proofreadingApplyConfirmDialog.note") }}
        </p>
        <div v-if="phase === 'applying'" class="dialog__loading">
          <LoadingSpinner size="medium" variant="inline" />
        </div>
      </div>

      <footer class="dialog__footer">
        <template v-if="phase === 'success'">
          <button type="button" class="dialog__ok-button" @click="onClickSuccessOk">
            {{ $t("proofreadingApplyConfirmDialog.ok") }}
          </button>
        </template>
        <template v-else>
          <button
            type="button"
            class="dialog__cancel-button"
            :disabled="phase === 'applying'"
            @click="onClickNo"
          >
            {{ $t("proofreadingApplyConfirmDialog.no") }}
          </button>
          <button
            type="button"
            class="dialog__ok-button"
            :disabled="phase === 'applying'"
            @click="onClickYes"
          >
            {{ primaryButtonText }}
          </button>
        </template>
      </footer>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import LoadingSpinner from "@/components/atoms/LoadingSpinner.vue";

type DialogPhase = "confirm" | "applying" | "success";

/**
 * 校正を原稿に反映する確認モーダル。
 *
 * Vue Modal Dialogs (`vue-modal-dialogs`) で `this.$close(value)` を呼ぶ前提:
 *  - 「はい」→反映成功→「OK」: `this.$close({ confirmed: true })`
 *  - 「いいえ」/ 外クリック / ESC（確認中のみ）: `this.$close({ confirmed: false })`
 *  - 反映中は外クリック・ESC では閉じない
 *  - `onConfirm` 指定時: 「はい」押下後に反映中 UI を表示し、完了後に成功画面へ遷移
 */
export default Vue.extend({
  components: {
    LoadingSpinner,
  },
  props: {
    onConfirm: {
      type: Function as PropType<() => Promise<void>>,
      default: undefined,
    },
  },
  data() {
    return {
      phase: "confirm" as DialogPhase,
    };
  },
  computed: {
    messageText(): string {
      if (this.phase === "success") {
        return this.$t("proofreadingApplyConfirmDialog.successMessage") as string;
      }
      return this.$t("proofreadingApplyConfirmDialog.message") as string;
    },
    primaryButtonText(): string {
      if (this.phase === "applying") {
        return this.$t("proofreadingApplyConfirmDialog.applying") as string;
      }
      return this.$t("proofreadingApplyConfirmDialog.yes") as string;
    },
  },
  mounted() {
    window.addEventListener("popstate", this.onPopState);
    this.$nextTick(() => {
      (this.$refs.dialogRef as HTMLElement | undefined)?.focus();
    });
  },
  beforeDestroy() {
    window.removeEventListener("popstate", this.onPopState);
  },
  methods: {
    onPopState() {
      // ブラウザ戻る/進む時は applying/success 中でもオーバーレイを残さない
      this.$close({ confirmed: false });
    },
    async onClickYes() {
      if (this.phase !== "confirm") return;

      if (!this.onConfirm) {
        this.$close({ confirmed: true });
        return;
      }

      this.phase = "applying";
      try {
        await this.onConfirm();
        this.phase = "success";
      } catch {
        // 呼び出し元でエラー通知済み。確認画面に戻して再試行可能にする。
        this.phase = "confirm";
      }
    },
    onClickNo() {
      if (this.phase !== "confirm") return;
      this.$close({ confirmed: false });
    },
    onClickSuccessOk() {
      this.$close({ confirmed: true });
    },
    onClickOutside() {
      if (this.phase !== "confirm") return;
      this.$close({ confirmed: false });
    },
    onKeydown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (this.phase === "applying") return;
      if (this.phase === "success") {
        this.onClickSuccessOk();
        return;
      }
      this.onClickNo();
    },
  },
});

export type ProofreadingApplyConfirmDialogResult = {
  confirmed: boolean;
};
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.mask {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9000;
  background-color: var(--color-overlay);
}

.dialog {
  display: flex;
  flex-direction: column;
  width: min(400px, 92vw);
  margin: 0;
  border: none;
  background-color: var(--color-background-normal);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);
  overflow: hidden;

  &:focus {
    outline: none;
  }
}

.dialog__body {
  padding: 24px 24px 8px;
}

.dialog__message {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  text-align: center;
  color: var(--color-text-normal);
  line-height: var(--line-height-default);
}

.dialog__note {
  margin: 12px 0 0;
  font-size: var(--font-size-sm);
  text-align: center;
  color: var(--color-text-low);
  line-height: var(--line-height-default);
  white-space: pre-line;
}

.dialog__loading {
  display: flex;
  justify-content: center;
  margin-top: 16px;

  ::v-deep .loading-spinner-container--inline {
    padding: 8px 0;
  }
}

.dialog__footer {
  display: flex;
  justify-content: center;
  gap: 12px;
  padding: 16px 24px 24px;
}

.dialog__cancel-button,
.dialog__ok-button {
  position: relative;
  flex: 1 1 0;
  min-width: 120px;
  padding: 10px 24px;
  border: none;
  border-radius: var(--radius-rounded);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;

    &:hover::after {
      background: transparent;
    }
  }
}

.dialog__cancel-button {
  background-color: var(--color-surface-muted);
  color: var(--color-text-normal);
}

.dialog__ok-button {
  background-color: var(--color-primary);
  color: var(--color-text-invert);
}
</style>
